import { useState } from 'react'

import {
  Box,
  Button,
  IllustrationType,
  Text,
  TextSpinner,
  Touchable,
} from '@factorialco/deprecated-design-system'
import { ProjectManagementProjectEmployeesAssignmentEnum } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import RadioInput from 'modules/core/components/Inputs/FilterSelect/SelectOption/RadioInput'
import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

export interface AssignEmployeesModalProps {
  iconName: IllustrationType
  title: string
  description: string | React.ReactNode
  loading: boolean
  showWarning: boolean
  submitButton: string
  employeesAssignment: ProjectManagementProjectEmployeesAssignmentEnum | undefined
  initialAssignedEmployeeIds: string[]
  onClose: () => void
  onSubmit: (
    initialEmployeesAssignment: ProjectManagementProjectEmployeesAssignmentEnum | undefined,
    newEmployeesAssignment: ProjectManagementProjectEmployeesAssignmentEnum,
    employeeSelection: EmployeeSelectorV2MultiSelectState
  ) => void
}

const AssignEmployeesModal = ({
  iconName,
  title,
  description,
  loading,
  showWarning,
  submitButton,
  employeesAssignment,
  initialAssignedEmployeeIds,
  onClose,
  onSubmit,
}: AssignEmployeesModalProps) => {
  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: initialAssignedEmployeeIds,
  })

  const [assignmentSelection, setAssignmentSelection] = useState<
    ProjectManagementProjectEmployeesAssignmentEnum | undefined
  >(employeesAssignment)

  const submitModal = () => {
    if (assignmentSelection === undefined) return

    onSubmit(employeesAssignment, assignmentSelection, employeeSelection)
  }

  const assignmentSelected = assignmentSelection !== undefined
  const isManualSelected = assignmentSelected && assignmentSelection === 'manual'
  const isCompanySelected = assignmentSelected && assignmentSelection === 'company'

  const disabledSubmitButton = !assignmentSelected || loading

  return (
    <Modal
      illustration={iconName}
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={submitModal}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={disabledSubmitButton} submit>
            {submitButton}
          </Button>
        </>
      }
    >
      {loading ? (
        <TextSpinner text={i18n.t('project_management.modals.assign_employees.spinner_message')} />
      ) : (
        <Box gap="s16">
          <Box
            gap="s16"
            border={{
              all: {
                width: 's2',
                style: 'solid',
                color: isCompanySelected ? 'viridian1000' : 'grey400',
              },
            }}
            borderRadius={{
              all: 'abs008',
            }}
            padding="s16"
          >
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal weight="semibold">
                {i18n.t('project_management.modals.assign_employees.options.company.title')}
              </Text.Normal>
              <Touchable
                onClick={() => setAssignmentSelection('company')}
                autofocus={isCompanySelected}
              >
                <RadioInput selected={isCompanySelected} />
              </Touchable>
            </Box>
            <Text.Normal>
              {i18n.t('project_management.modals.assign_employees.options.company.description')}
            </Text.Normal>
          </Box>
          <Box
            background="grey100"
            borderRadius={{
              all: 'abs008',
            }}
          >
            <Box
              gap="s16"
              border={{
                all: {
                  width: 's2',
                  style: 'solid',
                  color: isManualSelected ? 'viridian1000' : 'grey400',
                },
              }}
              borderRadius={{
                all: 'abs008',
              }}
              padding="s16"
              width="fit-content"
              background="grey000White"
            >
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Text.Normal weight="semibold">
                  {i18n.t('project_management.modals.assign_employees.options.manual.title')}
                </Text.Normal>
                <Touchable
                  onClick={() => setAssignmentSelection('manual')}
                  autofocus={isManualSelected}
                >
                  <RadioInput selected={isManualSelected} />
                </Touchable>
              </Box>
              <Text.Normal>
                {i18n.t('project_management.modals.assign_employees.options.manual.description')}
              </Text.Normal>
            </Box>
            {isManualSelected && (
              <Box padding="s16">
                <EmployeeSelectorV2
                  multiple
                  options={{ asList: true, disableSelectAll: true }}
                  value={initialAssignedEmployeeIds}
                  onChange={setEmployeeSelection}
                />
              </Box>
            )}
          </Box>
          {showWarning && (
            <Box
              gap="s16"
              padding="s16"
              borderRadius={{
                all: 'abs008',
              }}
              background="champagne400"
            >
              <Text.Normal>
                {i18n.t('project_management.modals.assign_employees.bulkWarning')}
              </Text.Normal>
            </Box>
          )}
        </Box>
      )}
    </Modal>
  )
}

export default observer(AssignEmployeesModal)
